/* ============================================================================
   FABRIC — Fabric (D 3468:242129 · variants FIONx 3468:242128 / FIBREx
   3468:242250 · M 3468:241645)

   Arc'teryx System_A, "The Sperro SV" (mission page), measured: one large
   looping plate; beside it the copy column holds sticky for the plate's
   full height; the thumbnails are tabs that swap the plate IN PLACE — the
   incoming layer fades in over 200ms on cubic-bezier(.87,-.01,.16,1), the
   outgoing one fades out 200ms later — and scrolling never changes the tab.
   Every loop is preloaded so the crossfade never shows an empty layer.
   The plates are ChatGPT-made motion loops of the Figma / wearfigs.com
   fabric stills (assets/fabric/); the fabric's logo sits over them as markup.
   Mobile: the same stage at 390 × 520 above the thumbnails, no sticky.
   ========================================================================== */

import { animate, MO, state, play, $, $$, mqDesktop } from '../10-pdp-prototype/mo.js';
import { FABRICS } from './data.js';

const loops = import.meta.glob('./assets/fabric/*-loop.{mp4,webm}', { eager: true, query: '?url', import: 'default' });
const posters = import.meta.glob('./assets/fabric/*-poster.{webp,jpg,png}', { eager: true, query: '?url', import: 'default' });
const thumbs = import.meta.glob('./assets/fabric/*-thumb.{webp,jpg,png}', { eager: true, query: '?url', import: 'default' });
/* logos dropped into assets/figma/ as fabric-<id>-logo.{svg,png} (FlexKnit™ from Dropbox) */
const logos = import.meta.glob('./assets/figma/fabric-*-logo.{svg,png}', { eager: true, query: '?url', import: 'default' });
const find = (map, id) => Object.entries(map).find(([k]) => k.includes(`/${id}-`))?.[1] || null;
const loopFor = (id, ext) => Object.entries(loops).find(([k]) => k.endsWith(`/${id}-loop.${ext}`))?.[1] || null;

const root = $('[data-fabric]');
const stage = $('[data-fabric-media]', root);
const tabs = $('[data-fabric-tabs]', root);
const panel = $('[data-fabric-panel]', root);
const EASE_IN = [0.87, -0.01, 0.16, 1];
const EASE_OUT = [0.42, 0, 0.58, 1];
let active = 0;

const crop = (c) => (c ? ` style="position:absolute;max-width:none;width:${c.w}%;height:${c.h}%;left:${c.l}%;top:${c.t}%"` : '');

function logoHTML(f) {
  /* the vector wins over the PNG */
  const mine = Object.entries(logos).filter(([k]) => k.includes(`/fabric-${f.id}-logo.`)).sort(([a], [b]) => b.endsWith('.svg') - a.endsWith('.svg'));
  const dropped = !f.logo && mine[0]?.[1];
  if (dropped) return `<img class="ncl-fplate__logo ncl-fplate__logo--${f.id}" src="${dropped}" alt="">`;
  if (f.logo) {
    const isSvg = f.logo.src.endsWith('.svg');
    return `<img class="ncl-fplate__logo ncl-fplate__logo--${f.id}" src="${f.logo.src}" alt="" ${isSvg ? 'data-svg' : ''}>`;
  }
  /* FORMx™ has no lockup yet: the same thin lowercase wordmark as FIONx's,
     set in FIGSv2 ExtraLight */
  return `<span class="ncl-fplate__word" aria-hidden="true">${f.word}${f.tm ? '<sup>x™</sup>' : ''}</span>`;
}

function plateHTML(f, i) {
  const mp4 = loopFor(f.id, 'mp4');
  const webm = loopFor(f.id, 'webm');
  const poster = find(posters, f.id) || f.still;
  const media = (mp4 || webm)
    ? `<video class="ncl-fplate__media" muted loop playsinline preload="none" poster="${poster}" data-fabric-video>
        ${webm ? `<source src="${webm}" type="video/webm">` : ''}${mp4 ? `<source src="${mp4}" type="video/mp4">` : ''}</video>`
    : `<picture class="ncl-fplate__pic">${f.stillMobile ? `<source media="(max-width: 779px)" srcset="${f.stillMobile}">` : ''}<img class="ncl-fplate__media" src="${f.still}" alt="" decoding="async" ${i ? 'loading="lazy"' : ''}></picture>`;
  return `
    <figure class="ncl-fplate" id="ncl-fplate-${f.id}" style="--plate-bg:${f.tone}" ${i === active ? 'data-active' : ''} aria-label="${f.name} fabric, up close">
      ${media}
      ${f.isNew && (mp4 || webm) ? '<span class="ncl-fplate__badge">NEW</span>' : ''}
      ${logoHTML(f)}
    </figure>`;
}

const nameHTML = (f) => `${f.name}${f.tm ? '<span class="ncl-tm">™</span>' : ''}`;

function render() {
  stage.innerHTML = FABRICS.map(plateHTML).join('');
  tabs.innerHTML = FABRICS.map((f, i) => `
    <button class="ncl-ftab" type="button" role="tab" id="ncl-ftab-${f.id}" aria-selected="${i === active}" tabindex="${i === active ? 0 : -1}"
      aria-controls="ncl-fabric-panel" aria-label="${f.name}${f.tm ? '™' : ''}" data-fabric-tab="${i}" data-fabric-reveal>
      ${find(thumbs, f.id) ? `<img src="${find(thumbs, f.id)}" alt="" decoding="async">` : `<img src="${f.thumb}" alt="" decoding="async"${crop(f.thumbCrop)}>`}
    </button>`).join('');
  panel.id = 'ncl-fabric-panel';
  fill(FABRICS[active]);
  syncVideos();
}

function fill(f) {
  $('[data-fabric-title]', panel).innerHTML = nameHTML(f);
  $('[data-fabric-body]', panel).textContent = f.body;
  $('[data-fabric-composition]', panel).innerHTML = f.composition.map((c) => `<li>${c}</li>`).join('');
  $('[data-fabric-properties]', panel).innerHTML = f.properties.map((c) => `<li>${c}</li>`).join('');
  /* only the CTAs the fabric has (FlexKnit™ is men's only on the site) */
  $$('[data-fabric-shop]', panel).forEach((a) => {
    const href = f.shop[a.dataset.fabricShop];
    a.hidden = !href;
    a.href = href || '#';
    const ext = /^https?:/.test(href || '');
    a.target = ext ? '_blank' : '';
    a.rel = ext ? 'noopener' : '';
  });
  panel.setAttribute('aria-labelledby', `ncl-ftab-${f.id}`);
}

function syncVideos() {
  $$('[data-fabric-video]', stage).forEach((v) => {
    const on = v.closest('.ncl-fplate').hasAttribute('data-active') && !state.reduced && inView;
    if (on) v.play().catch(() => {}); else v.pause();
  });
}

let inView = false;
async function select(i, { focus = false } = {}) {
  if (i === active) return;
  const prev = active;
  active = i;
  const f = FABRICS[i];
  $$('[data-fabric-tab]', tabs).forEach((t, k) => {
    t.setAttribute('aria-selected', String(k === i));
    t.tabIndex = k === i ? 0 : -1;
    if (k === i && focus) t.focus();
  });
  const plates = $$('.ncl-fplate', stage);
  const incoming = plates[i];
  const outgoing = plates[prev];
  /* Arc'teryx's layer swap: in on top, then the old one leaves */
  incoming.style.zIndex = '2';
  outgoing.style.zIndex = '1';
  incoming.setAttribute('data-active', '');
  const v = $('video', incoming);
  if (v) { try { v.currentTime = 0; } catch (e) { /* not loaded yet */ } }
  syncVideos();
  if (!state.reduced) {
    animate(incoming, { opacity: [0, 1] }, { duration: 0.2, ease: EASE_IN });
    animate(outgoing, { opacity: [1, 0] }, { duration: 0.2, delay: 0.2, ease: EASE_OUT }).finished.then(() => {
      if (active !== prev) { outgoing.removeAttribute('data-active'); outgoing.style.opacity = ''; }
      syncVideos();
    });
  } else {
    outgoing.removeAttribute('data-active');
    syncVideos();
  }
  /* the copy: out on exit/quick, in on entrance/base (no hard swap) */
  const parts = [...panel.children];
  await play(parts, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished;
  if (active !== i) return;
  fill(f);
  play(parts, { opacity: [0, 1], y: [6, 0] }, { duration: MO.base, ease: MO.eEntrance, delay: (k) => k * 0.03 });
}

function bind() {
  tabs.addEventListener('click', (e) => {
    const t = e.target.closest('[data-fabric-tab]');
    if (t) select(Number(t.dataset.fabricTab));
  });
  tabs.addEventListener('keydown', (e) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let n = null;
    if (e.key in keys) n = (active + keys[e.key] + FABRICS.length) % FABRICS.length;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = FABRICS.length - 1;
    if (n === null) return;
    e.preventDefault();
    select(n, { focus: true });
  });
  /* every loop preloads once the section is a screen away, so a tab's
     crossfade never waits on the network */
  new IntersectionObserver((es, io) => {
    if (!es[0].isIntersecting) return;
    $$('[data-fabric-video]', stage).forEach((v) => { v.preload = 'auto'; v.load(); });
    io.disconnect();
    syncVideos();
  }, { rootMargin: '100% 0px' }).observe(stage);
  /* loops only run while the stage is on screen */
  new IntersectionObserver(([e]) => { inView = e.isIntersecting; syncVideos(); }, { threshold: 0.05 }).observe(stage);
}

export function initFabric() {
  render();
  bind();
}
export const fabricSync = () => syncVideos();
